import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import API from '../services/config/api';
import '../styles/portal-auth.scss';
import PasswordEyeToggle from '../Components/PasswordEyeToggle';

function RiskPortalAuth() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const inviteToken = searchParams.get('token');
    const isExplicitLogin = location.pathname === '/risk-portal/login';

    const [mode, setMode] = useState(inviteToken ? 'register' : 'login');
    const [form, setForm] = useState({ email: '', password: '', confirm_password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        // On /risk-portal/login the user explicitly wants to sign in as a
        // portal-specific role — never auto-redirect, even if a dashboard
        // session exists.
        if (isExplicitLogin) return;

        const portalToken = localStorage.getItem('risk_portal_token');
        if (portalToken && !inviteToken) {
            navigate('/risk-portal');
            return;
        }

        const mainToken = sessionStorage.getItem('access_token');
        if (mainToken && !inviteToken) {
            const userProfile = JSON.parse(localStorage.getItem('user_profile') || '{}');
            localStorage.setItem('risk_portal_token', mainToken);
            localStorage.setItem('risk_portal_user', JSON.stringify({
                email: userProfile.emailId || userProfile.email || '',
                first_name: userProfile.first_name || '',
                last_name: userProfile.last_name || '',
                organization_id: userProfile.organization_id || '',
            }));
            sessionStorage.setItem('rp_from_dashboard', '1');
            navigate('/risk-portal');
        }
    }, [navigate, inviteToken, isExplicitLogin]);

    const handleLogin = async () => {
        if (!form.email || !form.password) { setError('Email and password are required.'); return; }
        setLoading(true); setError('');
        try {
            const axios = (await import('axios')).default;
            const authRes = await axios.post(
                API.RISK_PORTAL_LOGIN,
                { email: form.email, password: form.password },
                { headers: { 'Content-Type': 'application/json', 'Authorization': 'Basic YmFzZWFkbWluOmRlZmF1bHRANDU2' } }
            );
            const authData = authRes?.data?.response_data?.data;

            if (!authData?.access_token) {
                setError(authRes?.data?.response_data?.message || 'Invalid email or password.');
                setLoading(false);
                return;
            }

            sessionStorage.setItem('access_token', authData.access_token);
            if (authData.refresh_token) {
                sessionStorage.setItem('refresh_token', authData.refresh_token);
                localStorage.setItem('risk_portal_refresh', authData.refresh_token);
            }
            localStorage.setItem('user_profile', JSON.stringify(authData.user || {}));
            localStorage.setItem('risk_portal_token', authData.access_token);
            localStorage.setItem('risk_portal_user', JSON.stringify({
                email: authData.email || form.email,
                role: authData.role || '',
                first_name: authData.first_name || '',
                last_name: authData.last_name || '',
                organization_id: authData.organization_id || '',
            }));
            // Mark as portal-specific login so RiskPortal.js does not treat
            // sessionStorage.access_token as a main-dashboard session.
            sessionStorage.setItem('rp_from_dashboard', '0');
            navigate('/risk-portal');
        } catch (e) {
            const status = e?.response?.status;
            const errCode = e?.response?.data?.error?.error_code;
            const errMsg = e?.response?.data?.response_data?.message || e?.response?.data?.error?.message || '';
            if (status === 429 || errCode === 429) {
                setError('Too many login attempts. Please try again later.');
            } else {
                setError(errMsg || 'Invalid email or password.');
            }
        }
        setLoading(false);
    };

    const handleRegister = async () => {
        if (!form.password) { setError('Password is required.'); return; }
        if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
        if (form.password !== form.confirm_password) { setError('Passwords do not match.'); return; }
        setLoading(true); setError('');
        try {
            const { risk_portal_register } = await import('../services');
            const res = await risk_portal_register({ token: inviteToken, password: form.password, confirm_password: form.confirm_password });
            const data = res?.response_data?.data || res?.statusCode?.response_data?.data;
            if (data?.email) {
                setMessage(`Password set successfully for ${data.email}. You can now log in.`);
                setMode('login');
                setForm({ ...form, email: data.email, password: '', confirm_password: '' });
            } else {
                setError(res?.response_data?.message || res?.error?.message || 'Registration failed.');
            }
        } catch (e) {
            setError('Registration failed. The invite link may be expired.');
        }
        setLoading(false);
    };

    const features = [
        'End-to-end risk lifecycle management',
        'Severity-tiered risk acceptance workflow',
        'KRI tracking & treatment monitoring',
        'Role-based collaboration & sign-offs',
    ];

    return (
        <div className="portal-auth-wrapper">
            {/* Left branding panel */}
            <div className="portal-left-panel">
                <div className="portal-left-content">
                    <p className="portal-title-main">Risk</p>
                    <p className="portal-title-accent">Portal</p>
                    <p className="portal-tagline">
                        Centralised risk management, treatment tracking, and real-time reporting — all in one place.
                    </p>
                    <div className="portal-features">
                        {features.map((text, i) => (
                            <div key={i} className="portal-feature-item">
                                <span className="portal-check">✓</span>
                                <span>{text}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Right form panel */}
            <div className="portal-form-panel">
                <div className="portal-form-wrapper">
                    <div className="portal-logo-wrap">
                        <img src="/assets/small_logo.svg" alt="NMT" width={300} height={90} onError={e => { e.target.style.display = 'none'; }} />
                    </div>

                    <div className="portal-card">
                        <h1 className="portal-card-title">
                            {mode === 'register' ? 'Set Your Password' : 'Login'}
                        </h1>
                        <p className="portal-card-subtitle">
                            {mode === 'register'
                                ? 'Complete your registration to access the Risk Portal.'
                                : 'Enter your company email below to login to your account.'}
                        </p>

                        {message && <div className="portal-alert portal-alert--success">{message}</div>}
                        {error && <div className="portal-alert portal-alert--error">{error}</div>}

                        {mode === 'login' && (
                            <>
                                <div className="portal-form-group">
                                    <label htmlFor="rp-email">Work Email</label>
                                    <input
                                        id="rp-email"
                                        type="email"
                                        className="portal-input"
                                        value={form.email}
                                        onChange={e => setForm({ ...form, email: e.target.value })}
                                        onKeyDown={e => e.key === 'Enter' && handleLogin()}
                                        placeholder="user@company.com"
                                        autoFocus
                                    />
                                </div>

                                <div className="portal-form-group">
                                    <label htmlFor="rp-password">Password</label>
                                    <input
                                        id="rp-password"
                                        type={showPassword ? 'text' : 'password'}
                                        className="portal-input portal-input--with-icon"
                                        value={form.password}
                                        onChange={e => setForm({ ...form, password: e.target.value })}
                                        onKeyDown={e => e.key === 'Enter' && handleLogin()}
                                        placeholder="••••••••••"
                                    />
                                    <PasswordEyeToggle visible={showPassword} onClick={() => setShowPassword(v => !v)} className="portal-eye-btn" />
                                </div>

                                <div className="portal-forgot-row">
                                    <a href="/risk-portal/forgot-password">Forgot your password?</a>
                                </div>

                                <button className="portal-submit-btn" onClick={handleLogin} disabled={loading}>
                                    {loading ? 'Signing in...' : 'Login'}
                                </button>
                            </>
                        )}

                        {mode === 'register' && (
                            <>
                                <div className="portal-form-group">
                                    <label htmlFor="rp-new-pw">New Password</label>
                                    <input
                                        id="rp-new-pw"
                                        type="password"
                                        className="portal-input"
                                        value={form.password}
                                        onChange={e => setForm({ ...form, password: e.target.value })}
                                        placeholder="Minimum 8 characters"
                                    />
                                </div>

                                <div className="portal-form-group">
                                    <label htmlFor="rp-confirm-pw">Confirm Password</label>
                                    <input
                                        id="rp-confirm-pw"
                                        type="password"
                                        className="portal-input"
                                        value={form.confirm_password}
                                        onChange={e => setForm({ ...form, confirm_password: e.target.value })}
                                        onKeyDown={e => e.key === 'Enter' && handleRegister()}
                                        placeholder="Confirm password"
                                    />
                                    {form.confirm_password && form.password !== form.confirm_password && (
                                        <p className="portal-match-error">Passwords do not match.</p>
                                    )}
                                </div>

                                <button className="portal-submit-btn" onClick={handleRegister} disabled={loading}>
                                    {loading ? 'Setting password...' : 'Set Password & Continue'}
                                </button>
                            </>
                        )}

                        <div className="portal-card-footer">
                            NMT Security &nbsp;·&nbsp; Risk Portal
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RiskPortalAuth;
